import googleAnalytics, { type GAPluginConfig } from '@analytics/google-analytics';
import googleAnalyticsV3, { type GA3PluginConfig } from '@analytics/google-analytics-v3';
import snowplowPlugin, { type SnowplowPluginConfig } from '@analytics/snowplow';
import Analytics, { type AnalyticsInstance } from 'analytics';
import type { Restaurant } from '~/libs/gql/types';
import { type CurrentSession, currentSessionGlobal } from '~/shared/CurrentSessionProvider';
import type { PopmenuConfig } from '~/utils/withPopmenuConfig';
import { dangerouslyExecuteScripts } from './dom';
import { toCamelCase } from './strings';
import { runAfter } from './postponed';
import { restaurantGlobal } from './withRestaurant';

const createSnowplowTracker = (name: string, options: SnowplowPluginConfig, trackerOptions: SnowplowPluginConfig['trackerSettings']) => snowplowPlugin({
  name,
  scriptSrc: '/c.js',
  ...options,
  trackerSettings: {
    contexts: {
      webPage: true,
    },
    ...trackerOptions,
  },
});

const createGA3Tracker = (name: string, options: GA3PluginConfig) => ({
  ...googleAnalyticsV3({
    ...options,
    cookieConfig: {
      cookieFlags: 'SameSite=None; Secure',
    },
  }),
  name,
});

const createGA4Tracker = (name: string, options: GAPluginConfig) => ({
  ...googleAnalytics({
    ...options,
    gtagConfig: {
      cookie_flags: 'SameSite=None; Secure',
    },
  }),
  name,
});

let analytics: AnalyticsInstance | undefined;

const consumerAnalyticsConfig = (popmenuConfig: PopmenuConfig, restaurant?: Restaurant | null) => {
  const plugins = [];
  const globalGoogleAnalyticsEnabled = !restaurant || Boolean(restaurant?.featureSetting?.isGlobalGoogleAnalyticsEnabled);

  // Global multi-client tracking
  if (popmenuConfig) {
    // Snowplow
    if (popmenuConfig.snowplowUrl) {
      plugins.push(
        createSnowplowTracker('popmenu-consumer', { collectorUrl: popmenuConfig.snowplowUrl }, { appId: 'popmenu-consumer' }),
      );
    }

    // Global Popmenu GA3
    if (globalGoogleAnalyticsEnabled && popmenuConfig.googleAnalyticsId) {
      plugins.push(
        createGA3Tracker('google-analytics-popmenu', {
          instanceName: 'popmenuTracker',
          trackingId: popmenuConfig.googleAnalyticsId,
        }),
      );
    }

    // Global Popmenu GA4
    if (globalGoogleAnalyticsEnabled && popmenuConfig.ga4MeasurementId) {
      plugins.push(
        createGA4Tracker('google-analytics-4-popmenu', { measurementIds: [popmenuConfig.ga4MeasurementId] }),
      );
    }
  }

  // Restaurant-level tracking
  if (restaurant) {
    // Restaurant-level custom tracking
    if (restaurant.customTrackings) {
      restaurant.customTrackings.forEach((tracking) => {
        if (tracking.trackingType === 'google_tracking_type') {
          const id = `google-analytics-restaurant-${tracking.id}`;
          const instanceName = `restaurantTracker${tracking.id}`;

          if (tracking.trackingId.startsWith('G-')) {
            plugins.push(
              createGA4Tracker(id, {
                instanceName,
                measurementIds: [tracking.trackingId],
              }),
            );
          } else {
            plugins.push(
              createGA3Tracker(id, {
                instanceName,
                trackingId: tracking.trackingId,
              }),
            );
          }
        }
      });
    }

    // Restaurant-level Popmenu tracking
    if (restaurant.popmenuGoogleAnalyticsId) {
      plugins.push(
        createGA3Tracker('google-analytics-restaurant', {
          instanceName: 'popmenuRestaurantTracker',
          trackingId: restaurant.popmenuGoogleAnalyticsId,
        }),
      );
    }
  }

  return {
    app: 'popmenu-admin',
    plugins,
  };
};

const adminAnalyticsConfig = (popmenuConfig: PopmenuConfig) => {
  const plugins = [];

  if (popmenuConfig) {
    if (popmenuConfig.snowplowUrl) {
      plugins.push(
        createSnowplowTracker('popmenu-admin', { collectorUrl: popmenuConfig.snowplowUrl }, { appId: 'popmenu-admin' }),
      );
    }

    if (popmenuConfig.googleAdminAnalyticsId) {
      plugins.push(
        createGA3Tracker('google-analytics-popmenu-admin', { trackingId: popmenuConfig.googleAdminAnalyticsId }),
      );
    }

    if (popmenuConfig.ga4AdminMeasurementId) {
      plugins.push(
        createGA4Tracker('google-analytics-4-popmenu-admin', { measurementIds: [popmenuConfig.ga4AdminMeasurementId] }),
      );
    }
  }

  return {
    app: 'popmenu-admin',
    plugins,
  };
};

const isPopmenuUserAgent = () => Boolean(
  typeof navigator !== 'undefined' &&
  navigator.userAgent &&
  (navigator.userAgent.includes('Popmenu') || navigator.userAgent.includes('Lighthouse') || navigator.userAgent.includes('Page Speed')),
);

export interface InitAnalyticsOptions {
  adminApp: boolean;
  popmenuConfig: PopmenuConfig;
  restaurant?: Restaurant | null;
}

// Initialize GA and Snowplow trackers
export const initAnalyticsCallback = ({
  adminApp,
  popmenuConfig,
  restaurant,
}: InitAnalyticsOptions) => {
  if (typeof window !== 'undefined') {
    if (restaurantGlobal?.draftMode) {
      console.warn('[POPMENU][ANALYTICS] Skipped initializing analytics in draft mode');
      return;
    }

    // Skip tracking for Lighthouse and other automated runs
    if (isPopmenuUserAgent()) {
      console.warn(`[POPMENU][ANALYTICS] Skipped tracking for user-agent ${navigator.userAgent}`);
      return;
    }

    void runAfter('firstUserInteraction', () => {
      try {
        const { app, plugins } = adminApp ?
          adminAnalyticsConfig(popmenuConfig) :
          consumerAnalyticsConfig(popmenuConfig, restaurant);
        console.log(`[POPMENU][ANALYTICS] Add plugins x${plugins.length}`);
        analytics = Analytics({
          app,
          plugins,
          version: popmenuConfig?.popmenuVersion || 'null',
        });

        // Enable some automatic tracking before page event
        analytics.on('initialize:snowplow', ({ instance }: { instance: AnalyticsInstance }) => {
          // @ts-expect-error instance.plugins is not typed correctly
          const snowplow = instance.plugins.snowplow as {
            // https://github.com/DavidWells/analytics/blob/545b3721a37101aa1c7ce66cea755a58b2e518ab/packages/analytics-plugin-snowplow/src/browser.js#L314-L321
            enableActivityTracking: (minimumVisitLength: number, heartbeat: number) => void;
            enableLinkClickTracking: () => void;
          };
          snowplow.enableActivityTracking(10, 10);
          snowplow.enableLinkClickTracking();
        });

        console.log('[POPMENU][ANALYTICS] Analytics initialized');
      } catch (err) {
        console.warn('[POPMENU][ANALYTICS] Error initializing analytics:', err, (err as Error).stack);
      }
    });
  }
};

// We could infer them using https://github.com/ThomasAribart/json-schema-to-ts,
// but this is simple enough and they rarely change.
export type SnowplowContext = {
  schema: 'iglu:com.popmenu/dish/jsonschema/1-0-0' |
  'iglu:com.popmenu/menu/jsonschema/1-0-0' |
  'iglu:com.popmenu/menu_item/jsonschema/1-0-0' |
  'iglu:com.popmenu/restaurant/jsonschema/1-0-0' |
  'iglu:com.popmenu/user/jsonschema/1-0-0';
  data: {
    id: number;
  };
} | {
  schema: 'iglu:com.popmenu/session/jsonschema/1-0-1';
  data: {
    id: string;
  };
} | {
  schema: 'iglu:com.popmenu/blog_post/jsonschema/1-0-1' | 'iglu:com.popmenu/calendar_event/jsonschema/1-0-1';
  data: {
    slug: string;
  };
} | {
  schema: 'iglu:com.popmenu/custom_page/jsonschema/1-0-1';
  data: {
    restaurantId: number;
    slug: string;
  };
};

const getSnowplowContexts = (restaurant: Restaurant | null, session: CurrentSession) => {
  const snowplowContexts: SnowplowContext[] = [];

  const attachedRestaurantId = restaurant?.id;
  if (attachedRestaurantId) {
    snowplowContexts.push({
      data: {
        id: attachedRestaurantId,
      },
      schema: 'iglu:com.popmenu/restaurant/jsonschema/1-0-0',
    });
  }

  if (session.id) {
    snowplowContexts.push({
      data: {
        id: session.id,
      },
      schema: 'iglu:com.popmenu/session/jsonschema/1-0-1',
    });
  }

  if (session.user?.id) {
    snowplowContexts.push({
      data: {
        id: session.user.id,
      },
      schema: 'iglu:com.popmenu/user/jsonschema/1-0-0',
    });
  }

  return snowplowContexts;
};

export type EventVariables = {
  eventableType: string;
  eventType: string;
  eventableId?: number;
  eventLabel?: string;
  [variable: string]: unknown;
};

export const createEventCallback = (
  variables: EventVariables,
) => {
  if (restaurantGlobal?.draftMode) {
    return;
  }

  void runAfter('firstUserInteraction', () => {
    // Push event to analytics
    if (analytics) {
      const snowplowEventContexts = getSnowplowContexts(restaurantGlobal, currentSessionGlobal);

      void analytics.track(
        toCamelCase(variables.eventType),
        {
          category: variables.eventableType,
          contexts: snowplowEventContexts,
          label: variables.eventLabel,
        },
      );
    } else {
      console.warn('[POPMENU][ANALYTICS] Analytics not initialized in createEventCallback');
    }

    // Execute the conversion script for specific event types
    const conversionName = {
      call_attempt_event: 'call',
      pop_attempt_event: 'pop',
      reserve_attempt_event: 'reserve',
      review_attempt_event: 'review',
    }[variables.eventType];
    const googleAdsConversionActionScripts = restaurantGlobal?.googleAdsConversionActionScripts;
    if (conversionName && googleAdsConversionActionScripts) {
      // Execute the Google Ads conversion script after certain events
      googleAdsConversionActionScripts.forEach((script) => {
        if (script.name?.toLowerCase() === conversionName) {
          const container = document.createElement('div');
          container.innerHTML = script.tag;
          document.body.appendChild(container);
          dangerouslyExecuteScripts(container, `${script.name}${script.id}`);
        }
      });
    }
  });
};

export const createPageViewCallback = (pageSnowplowContexts: SnowplowContext[]) => {
  if (restaurantGlobal?.draftMode) {
    return;
  }

  if (isPopmenuUserAgent()) {
    return;
  }

  void runAfter('firstUserInteraction', () => {
    // Push the visit to analytics
    if (analytics) {
      const path = window.location.href.split(window.location.hostname, 2)[1];

      const snowplowContexts = [...getSnowplowContexts(restaurantGlobal, currentSessionGlobal), ...pageSnowplowContexts];

      void analytics.page({
        contexts: snowplowContexts,
        path,
      });
    } else {
      console.warn('[POPMENU][ANALYTICS] Analytics not initialized in createPageViewCallback');
    }
  });
};
